import React from 'react';
import { t } from '../../services/HelperService';
import {
  DOCUMENT_KIND_IMAGE, DOCUMENT_STATUS_FAILED, DOCUMENT_STATUS_PENDING, DOCUMENT_STATUS_PROCESSING, DOCUMENT_STATUS_READY,
  attachAssistantSessionFiles, confirmAssistantSessionDocument, fetchAssistantSessionDocument, listAssistantSessionDocuments,
  pasteAssistantSessionText, promoteAssistantSessionDocument, removeAssistantSessionDocument,
} from './assistantSessionApi';
import { ATTACHMENT_STATUS_UPLOADING } from './AttachmentChip';

const safeString = (v) => (v ?? '').toString();
const POLL_MS = 1500;
const GLOW_MS = 4000;

let localSeq = 0;
const newLocalId = () => `local:${Date.now()}_${(localSeq += 1)}`;
// A chip the server does not have: an upload in flight here, or a message-only screenshot the composer shows beside the files.
const isClientOnly = (a) => { const id = safeString(a?.id); return id.startsWith('local:') || id.startsWith('msg:'); };

const isWorking = (d) => d.status === DOCUMENT_STATUS_PENDING || d.status === DOCUMENT_STATUS_PROCESSING;

/** A default name for pasted text: its first line, trimmed to something chip-sized. */
export const pasteName = (text) => {
  const firstLine = safeString(text).split(/\r?\n/).map((l) => l.trim()).find((l) => l.length > 0) || t('Pasted text');
  const stem = firstLine.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40).trim();
  return `${stem || t('Pasted text')}.txt`;
};

/**
 * The session's files as the composer's chips, kept current against the server:
 * optimistic chips while an upload / paste is in flight, polling while anything is
 * being read, a glow on the files the assistant read this turn, thumbnails for images,
 * and the actions (attach, paste, remove, confirm, keep, open). One instance per
 * mounted dialog body; the session key changes remount the body, so it starts clean.
 *
 * Options:
 * - sessionKey: the server-kept session (nothing happens without one)
 * - onKept(savedAsset, attachment): a file was kept as a library entry
 * - notify(kind, text): the host's message pusher ('error' | 'system')
 */
export const useAssistantSessionDocuments = ({ sessionKey, onKept, notify } = {}) => {
  const key = safeString(sessionKey).trim();
  const [docs, setDocs] = React.useState([]);
  const [local, setLocal] = React.useState([]);
  const [keptKeys, setKeptKeys] = React.useState({});
  const [glowing, setGlowing] = React.useState({});
  const [thumbs, setThumbs] = React.useState({});
  const thumbsRef = React.useRef({});
  const aliveRef = React.useRef(true);
  const onKeptRef = React.useRef(onKept);
  onKeptRef.current = onKept;
  const notifyRef = React.useRef(notify);
  notifyRef.current = notify;

  const say = React.useCallback((kind, text) => {
    try { notifyRef.current?.(kind, text); } catch { /* the host's pusher is best effort */ }
  }, []);

  const reload = React.useCallback(async () => {
    if (!key) return [];
    try {
      const list = await listAssistantSessionDocuments(key);
      if (aliveRef.current) setDocs(list);
      return list;
    } catch {
      return null;
    }
  }, [key]);

  React.useEffect(() => {
    aliveRef.current = true;
    setDocs([]);
    setLocal([]);
    setKeptKeys({});
    reload();
    return () => {
      aliveRef.current = false;
      for (const url of Object.values(thumbsRef.current)) {
        try { URL.revokeObjectURL(url); } catch { /* already gone */ }
      }
      thumbsRef.current = {};
    };
  }, [key, reload]);

  // Poll while anything is being read: the chip's page count moves and the
  // assistant's SESSION FILES index is only as current as this list.
  const working = docs.some(isWorking);
  React.useEffect(() => {
    if (!key || !working) return undefined;
    const timer = setInterval(reload, POLL_MS);
    return () => clearInterval(timer);
  }, [key, reload, working]);

  // Thumbnails for image files (the API wants the bearer token, so an <img src> cannot).
  React.useEffect(() => {
    if (!key) return;
    for (const d of docs) {
      if (d.kind !== DOCUMENT_KIND_IMAGE || thumbsRef.current[d.id] !== undefined) continue;
      thumbsRef.current[d.id] = '';
      fetchAssistantSessionDocument(key, d.id).then((res) => {
        if (!aliveRef.current || !res?.url) return;
        thumbsRef.current[d.id] = res.url;
        setThumbs((prev) => ({ ...prev, [d.id]: res.url }));
      }).catch(() => { /* no thumbnail, the icon stands in */ });
    }
  }, [docs, key]);

  const markRead = React.useCallback((names) => {
    const list = (Array.isArray(names) ? names : []).map(safeString).filter(Boolean);
    if (list.length === 0) return;
    setGlowing((prev) => {
      const next = { ...prev };
      for (const n of list) next[n.toLowerCase()] = Date.now();
      return next;
    });
    setTimeout(() => {
      if (!aliveRef.current) return;
      setGlowing((prev) => {
        const cutoff = Date.now() - GLOW_MS + 50;
        const next = {};
        for (const [n, at] of Object.entries(prev)) if (at > cutoff) next[n] = at;
        return next;
      });
    }, GLOW_MS);
  }, []);

  const updateLocal = React.useCallback((id, patch) => {
    setLocal((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);
  const dropLocal = React.useCallback((id) => {
    setLocal((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const attachFiles = React.useCallback(async (files) => {
    const list = Array.from(files ?? []).filter(Boolean);
    if (!key || list.length === 0) return;
    const placeholders = list.map((f) => ({
      id: newLocalId(), file: f, name: f.name, kind: safeString(f.type).startsWith('image/') ? DOCUMENT_KIND_IMAGE : 'text',
      status: ATTACHMENT_STATUS_UPLOADING, progress: 0, bytes: f.size, removable: false,
    }));
    setLocal((prev) => [...prev, ...placeholders]);
    for (const p of placeholders) {
      try {
        const added = await attachAssistantSessionFiles(key, [p.file], {
          onProgress: (_file, sent, total) => updateLocal(p.id, { progress: total > 0 ? sent / total : 0 }),
        });
        if (!aliveRef.current) return;
        setDocs((prev) => {
          const ids = new Set((added ?? []).map((d) => d.id));
          return [...prev.filter((d) => !ids.has(d.id)), ...(added ?? [])];
        });
        dropLocal(p.id);
      } catch (err) {
        if (!aliveRef.current) return;
        updateLocal(p.id, { status: DOCUMENT_STATUS_FAILED, failure: safeString(err?.message) || t('could not be uploaded.'), removable: true });
      }
    }
    reload();
  }, [dropLocal, key, reload, updateLocal]);

  const dropText = React.useCallback(async (text, html, name) => {
    const body = safeString(text);
    if (!key || body.trim().length === 0) return;
    const chipName = safeString(name).trim() || pasteName(body);
    const id = newLocalId();
    setLocal((prev) => [...prev, { id, name: chipName, kind: 'paste', status: ATTACHMENT_STATUS_UPLOADING, progress: 0.5, bytes: body.length, removable: false }]);
    try {
      const added = await pasteAssistantSessionText(key, { name: chipName, text: body, html: safeString(html) || null });
      if (!aliveRef.current) return;
      if (added) setDocs((prev) => [...prev.filter((d) => d.id !== added.id), added]);
      dropLocal(id);
    } catch (err) {
      if (!aliveRef.current) return;
      updateLocal(id, { status: DOCUMENT_STATUS_FAILED, failure: safeString(err?.message) || t('could not be kept.'), removable: true });
    }
  }, [dropLocal, key, updateLocal]);

  const remove = React.useCallback(async (a) => {
    if (!a) return;
    if (isClientOnly(a)) { if (safeString(a.id).startsWith('local:')) dropLocal(a.id); return; }
    let before = null;
    setDocs((prev) => { before = prev; return prev.filter((d) => d.id !== a.id); });
    try {
      await removeAssistantSessionDocument(key, a.id);
    } catch (err) {
      if (!aliveRef.current) return;
      if (before) setDocs(before);
      say('error', `${t('The file could not be removed')}: ${safeString(err?.message) || safeString(err)}`);
    }
  }, [dropLocal, key, say]);

  const confirm = React.useCallback(async (a) => {
    if (!a || isClientOnly(a)) return;
    setDocs((prev) => prev.map((d) => (d.id === a.id ? { ...d, status: DOCUMENT_STATUS_PROCESSING, holdReason: '', failure: '' } : d)));
    try {
      const next = await confirmAssistantSessionDocument(key, a.id);
      if (!aliveRef.current) return;
      if (next) setDocs((prev) => prev.map((d) => (d.id === next.id ? next : d)));
      else reload();
    } catch (err) {
      if (!aliveRef.current) return;
      reload();
      say('error', `${t('The file could not be read')}: ${safeString(err?.message) || safeString(err)}`);
    }
  }, [key, reload, say]);

  const keep = React.useCallback(async (a, { key: assetKey, description } = {}) => {
    if (!a || isClientOnly(a)) return null;
    const saved = await promoteAssistantSessionDocument(key, a.id, { key: assetKey, description });
    if (!saved || !saved.Key) throw new Error(t('The reference could not be saved.'));
    if (aliveRef.current) setKeptKeys((prev) => ({ ...prev, [a.id]: saved.Key }));
    try { onKeptRef.current?.(saved, a); } catch { /* the host's own bookkeeping */ }
    return saved;
  }, [key]);

  const open = React.useCallback(async (a) => {
    if (!a || isClientOnly(a)) return;
    try {
      const res = await fetchAssistantSessionDocument(key, a.id);
      if (!res?.url) { say('error', t('The file could not be opened.')); return; }
      window.open(res.url, '_blank', 'noopener');
      setTimeout(() => { try { URL.revokeObjectURL(res.url); } catch { /* fine */ } }, 60000);
    } catch (err) {
      say('error', `${t('The file could not be opened')}: ${safeString(err?.message) || safeString(err)}`);
    }
  }, [key, say]);

  const attachments = React.useMemo(() => {
    const server = [...docs].sort((x, y) => (y.added?.getTime?.() ?? 0) - (x.added?.getTime?.() ?? 0)).map((d) => ({
      ...d,
      keptKey: keptKeys[d.id] || undefined,
      glow: !!glowing[safeString(d.name).toLowerCase()],
      thumb: thumbs[d.id] || undefined,
      removable: true,
    }));
    return [...local, ...server];
  }, [docs, glowing, keptKeys, local, thumbs]);

  const readyCount = docs.filter((d) => d.status === DOCUMENT_STATUS_READY).length;

  return {
    enabled: !!key,
    attachments,
    readyCount,
    working: working || local.some((l) => l.status === ATTACHMENT_STATUS_UPLOADING),
    reload,
    attachFiles,
    dropText,
    remove,
    confirm,
    keep,
    open,
    markRead,
  };
};
